// mobile-player-seek — lógica PURA dos gestos de seek do player MOBILE. Parede A4.
//
// Separada do componente (mesmo padrão de onboarding-swipe.ts / mobile-shell-view.ts)
// pra ser testável SEM DOM: o MobilePlayer só liga estes cálculos a eventos de
// toque. Cobre:
//   - double-tap nas metades esquerda/direita do vídeo (-/+ 10s),
//   - posição do scrubber arrastável ↔ tempo,
//   - clamp de tempo dentro de [0, duração].
//
// Nada aqui toca em <video>, React ou window — só matemática + tipos. O player
// pega o resultado e aplica em video.currentTime / na barra.

/** Salto padrão por double-tap (segundos). Padrão nativo (YouTube/Globoplay). */
export const DOUBLE_TAP_SEEK_SECONDS = 10

/** Tipos de conteúdo que o player mobile sabe abrir. Live não tem seek. */
export type SeekableKind = 'vod' | 'series' | 'channel'

/** True quando o conteúdo tem seek (scrubber + botões/double-tap de ±10s).
 *  VOD e série têm; live (channel) não. Pura pra ser testável e pra o player e
 *  os controles (scrubber, botões de skip) decidirem do MESMO jeito. */
export function hasSeekControls(kind: SeekableKind): boolean {
  return kind !== 'channel'
}

/** Janela máxima (ms) entre dois taps pra contar como double-tap. */
export const DOUBLE_TAP_WINDOW_MS = 300

/** Lado do vídeo tocado — decide o sinal do salto no double-tap. */
export type SeekSide = 'left' | 'right'

/** Limita um tempo (segundos) dentro de [0, duração]. Defensivo contra
 *  duração 0/NaN/Infinity (stream ao vivo, metadata ainda não carregada). */
export function clampTime(sec: number, duration: number): number {
  if (!Number.isFinite(sec)) return 0
  const max = Number.isFinite(duration) && duration > 0 ? duration : 0
  if (sec < 0) return 0
  if (sec > max) return max
  return max > 0 ? sec : 0
}

/** Em qual metade do vídeo o toque caiu, a partir do X relativo ao retângulo
 *  do vídeo. clientX já vem normalizado pelo caller (clientX - rect.left). */
export function seekSideFromX(localX: number, width: number): SeekSide {
  if (!Number.isFinite(width) || width <= 0) return 'right'
  return localX < width / 2 ? 'left' : 'right'
}

/** Novo tempo após um double-tap. Esquerda recua, direita avança, com clamp.
 *  step default = DOUBLE_TAP_SEEK_SECONDS (parametrizável p/ testes/ajuste). */
export function seekAfterDoubleTap(
  current: number,
  duration: number,
  side: SeekSide,
  step: number = DOUBLE_TAP_SEEK_SECONDS,
): number {
  const delta = side === 'left' ? -step : step
  return clampTime(current + delta, duration)
}

/** True se dois taps no MESMO lado, dentro da janela, formam um double-tap.
 *  prev = null (sem tap anterior) → nunca é double-tap. */
export function isDoubleTap(
  prev: { ts: number; side: SeekSide } | null,
  next: { ts: number; side: SeekSide },
  windowMs: number = DOUBLE_TAP_WINDOW_MS,
): boolean {
  if (!prev) return false
  if (prev.side !== next.side) return false
  const dt = next.ts - prev.ts
  return dt >= 0 && dt <= windowMs
}

/** Fração (0..1) de uma posição X dentro de uma barra, dado o X relativo à
 *  borda esquerda da barra e a largura. Clampa nas bordas. */
export function barFraction(localX: number, width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 0
  const f = localX / width
  if (f < 0) return 0
  if (f > 1) return 1
  return f
}

/** Tempo (segundos) correspondente a uma fração do scrubber. */
export function timeFromFraction(fraction: number, duration: number): number {
  return clampTime(fraction * duration, duration)
}

/** Fração (0..1) preenchida da barra dado o tempo atual e a duração. Usado pra
 *  largura do "fill" e posição do "thumb". Live/duração 0 → 0. */
export function fractionFromTime(current: number, duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0
  return barFraction(current, duration)
}

/** Estados de reprodução do player mobile. 'buffering' = re-buffer no MEIO (já
 *  houve play); 'ended' = VOD terminou. Pura pra o reducer de buffering abaixo
 *  ser testável sem DOM. */
export type MobilePlayerStatus = 'loading' | 'playing' | 'paused' | 'buffering' | 'ended' | 'error'

/** Próximo status quando o core emite 'buffering'. Re-buffer NÃO pode sobrescrever
 *  estados terminais (fim do conteúdo / erro) — senão um stall tardio mostraria
 *  spinner em cima do card de fim/erro.
 *  Nem a CARGA (`loading`): no mp4 e no HLS nativo o `ready` sai logo depois do
 *  `src`, o `play()` do ready dispara `waiting` ~1 ms depois e o core emite
 *  'buffering' ANTES do 1º frame. Virar 'buffering' ali trocava o overlay cheio da
 *  carga (spinner + frase + aviso dos 60 s + ‹) pelo selo do canto em ~300 ms
 *  (review 10/10/2026). Aqui a carga só sai de 'loading' quando o core emite
 *  'playing' (ou 'paused'/'ended'); o 'error' do MobilePlayer vem só do sign/load
 *  ou da conta vencida. ERRO DE MÍDIA não encerra a carga: o MobilePlayer não escuta
 *  `player.on('error')` (AP-125, anterior a esta mudança) — no WebKit a frase segue
 *  até o aviso dos 60 s; no Chromium o `pause` que vem depois do erro vira 'paused'.
 *  Nos demais casos (re-buffer DEPOIS de tocar) vira 'buffering' (o próximo
 *  'playing' do core devolve pra 'playing'). */
export function nextStatusOnBuffering(prev: MobilePlayerStatus): MobilePlayerStatus {
  if (prev === 'ended' || prev === 'error' || prev === 'loading') return prev
  return 'buffering'
}
